import { IMPLEMENTED_HOUSE_IDS } from "./implemented-houses.ts";
import {
  gameConfigSchema,
  HOUSE_IDS,
  remoteConfigOverrideSchema,
  type FeatureFlags,
  type GameConfig,
  type HouseId,
  type HouseManifest,
  type HouseStatus,
  type RemoteConfigOverride,
} from "./schema.ts";

const implemented = new Set<HouseId>(IMPLEMENTED_HOUSE_IDS);

export function parseGameConfig(input: unknown): GameConfig {
  return gameConfigSchema.parse(input);
}

export function parseRemoteOverride(input: unknown): RemoteConfigOverride {
  return remoteConfigOverrideSchema.parse(input);
}

export function isHouseImplemented(houseId: HouseId): boolean {
  return implemented.has(houseId);
}

/**
 * Remote values may only disable. An unimplemented house never becomes
 * `available`, even if static config or remote config claims it is.
 */
export function resolveHouseStatus(
  packaged: HouseStatus,
  remote: HouseStatus | undefined,
  houseId: HouseId,
): HouseStatus {
  const local = isHouseImplemented(houseId) ? packaged : "under_construction";

  if (remote === undefined) {
    return local;
  }

  if (remote === "available") {
    return local === "available" ? "available" : local;
  }

  return remote;
}

export function resolveFlag(packaged: boolean, remote: boolean | undefined): boolean {
  if (remote === undefined || packaged === false) {
    return packaged;
  }

  return packaged && remote;
}

export function resolveGameConfig(
  packaged: GameConfig,
  remote?: RemoteConfigOverride,
): GameConfig {
  const flags: FeatureFlags = {
    leaderboardEnabled: resolveFlag(
      packaged.flags.leaderboardEnabled,
      remote?.leaderboardEnabled,
    ),
    shopEnabled: resolveFlag(packaged.flags.shopEnabled, remote?.shopEnabled),
    cloudSyncEnabled: resolveFlag(
      packaged.flags.cloudSyncEnabled,
      remote?.cloudSyncEnabled,
    ),
  };

  const houses = HOUSE_IDS.map((id) => {
    const house = packaged.houses.find((item) => item.id === id);
    if (!house) {
      throw new Error(`Missing house metadata: ${id}`);
    }

    return {
      ...house,
      status: resolveHouseStatus(house.status, remote?.houses?.[id], house.id),
    };
  });

  return parseGameConfig({
    ...packaged,
    flags,
    houses,
  });
}

export function getHouse(config: GameConfig, houseId: HouseId): HouseManifest {
  const house = config.houses.find((item) => item.id === houseId);
  if (!house) {
    throw new Error(`Missing house metadata: ${houseId}`);
  }
  return house;
}

export function isHousePlayable(config: GameConfig, houseId: HouseId): boolean {
  return (
    isHouseImplemented(houseId) && getHouse(config, houseId).status === "available"
  );
}

export function playableHouseIds(config: GameConfig): HouseId[] {
  return HOUSE_IDS.filter((id) => isHousePlayable(config, id));
}
